/**
 * MarketingLayout — MADE-303
 *
 * Shared wrapper for all public Offshore Sails marketing pages.
 * Provides:
 *   • A scoped MUI ThemeProvider (navy/gold/Montserrat — no impact on staff pages)
 *   • Sticky nav: logo, Home/About/Sails/Contact links, Staff Login text link
 *   • Responsive hamburger drawer for mobile
 *   • Footer: email + copyright (no fabricated address or phone)
 */

import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  AppBar, Box, Button, Container, CssBaseline, Drawer, IconButton,
  Link, List, ListItem, ListItemButton, ListItemText, Stack, Toolbar, Typography,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { Link as RouterLink, useLocation } from 'react-router-dom';

// ── Brand theme (scoped to marketing pages only) ─────────────────────────────
const marketingTheme = createTheme({
  palette: {
    primary: { main: '#002680' },
    secondary: { main: '#FFC726' },
    background: { default: '#ffffff', paper: '#ffffff' },
    text: { primary: '#002680', secondary: '#444444' },
  },
  typography: {
    fontFamily: '"Montserrat", "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    body1: { fontWeight: 500 },
    body2: { fontWeight: 500 },
  },
});

const NAV_LINKS = [
  { label: 'Home',    to: '/os' },
  { label: 'About',   to: '/os/about' },
  { label: 'Sails',   to: '/os/sails' },
  { label: 'Contact', to: '/os/contact' },
];

interface MarketingLayoutProps {
  children: React.ReactNode;
  /** Document title suffix, e.g. "About Us" → "About Us — Offshore Sails" */
  pageTitle?: string;
}

export const MarketingLayout: React.FC<MarketingLayoutProps> = ({ children, pageTitle }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const base = 'Offshore Sails';
    document.title = pageTitle ? `${pageTitle} | ${base}` : base;
    return () => { document.title = 'Offshore Sails Online'; };
  }, [pageTitle]);

  // MADE-303 AC#11 — Umami pageview on every client-side route change
  React.useEffect(() => {
    let tracked = false;
    const trackMarketingPage = () => {
      if (tracked) return;
      const tracker = (window as Window & { umami?: { track: () => void } }).umami;
      if (!tracker) return;
      tracker.track();
      tracked = true;
    };
    window.addEventListener('madebb-umami-ready', trackMarketingPage);
    trackMarketingPage();
    return () => window.removeEventListener('madebb-umami-ready', trackMarketingPage);
  }, [location.pathname]);

  const isActive = (to: string) =>
    to === '/os' ? location.pathname === '/os' : location.pathname.startsWith(to);

  const closeDrewer = () => setDrawerOpen(false);

  return (
    <ThemeProvider theme={marketingTheme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'white' }}>

        {/* ── Navigation bar ────────────────────────────────────────────── */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid rgba(0,38,128,0.10)',
          }}
        >
          <Toolbar
            sx={{
              maxWidth: 1200,
              width: '100%',
              mx: 'auto',
              px: { xs: 2, md: 4 },
              minHeight: { xs: 64, md: 72 },
            }}
          >
            {/* Logo — links to home */}
            <Box
              component={RouterLink}
              to="/os"
              aria-label="Offshore Sails home"
              sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', mr: 'auto' }}
            >
              <Box
                component="img"
                src="/os/Offshore Sails-Horizontal-MainColor.svg"
                alt="Offshore Sails"
                sx={{ height: { xs: 36, md: 44 } }}
              />
            </Box>

            {/* Desktop nav */}
            <Stack
              direction="row"
              spacing={0}
              alignItems="center"
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              {NAV_LINKS.map(({ label, to }) => (
                <Button
                  key={to}
                  component={RouterLink}
                  to={to}
                  disableRipple
                  sx={{
                    color: isActive(to) ? '#002680' : '#555',
                    fontWeight: isActive(to) ? 700 : 500,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    px: 2,
                    py: 0.75,
                    borderRadius: 0,
                    borderBottom: isActive(to) ? '2px solid #FFC726' : '2px solid transparent',
                    '&:hover': { bgcolor: 'transparent', color: '#002680' },
                  }}
                >
                  {label}
                </Button>
              ))}

              {/* Staff login — unobtrusive */}
              <Button
                component={RouterLink}
                to="/os/login"
                disableRipple
                sx={{
                  ml: 3,
                  color: '#888',
                  fontWeight: 500,
                  fontSize: '0.78rem',
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'transparent', color: '#002680' },
                }}
              >
                Staff Login
              </Button>
            </Stack>

            {/* Mobile hamburger */}
            <IconButton
              onClick={() => setDrawerOpen(true)}
              aria-label="Open navigation menu"
              sx={{ display: { xs: 'flex', md: 'none' }, color: '#002680' }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* ── Mobile drawer ─────────────────────────────────────────────── */}
        <Drawer anchor="right" open={drawerOpen} onClose={closeDrewer}>
          <Box sx={{ width: 220, pt: 2 }} role="navigation" aria-label="Mobile navigation">
            <List>
              {NAV_LINKS.map(({ label, to }) => (
                <ListItem key={to} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={to}
                    onClick={closeDrewer}
                    selected={isActive(to)}
                    sx={{ '&.Mui-selected': { bgcolor: 'rgba(0,38,128,0.06)' } }}
                  >
                    <ListItemText
                      primary={label}
                      primaryTypographyProps={{
                        fontWeight: isActive(to) ? 700 : 500,
                        color: '#002680',
                        fontSize: '1rem',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              <ListItem disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to="/os/login"
                  onClick={closeDrewer}
                >
                  <ListItemText
                    primary="Staff Login"
                    primaryTypographyProps={{ fontSize: '0.85rem', color: '#888' }}
                  />
                </ListItemButton>
              </ListItem>
            </List>
          </Box>
        </Drawer>

        {/* ── Page content ──────────────────────────────────────────────── */}
        <Box component="main" sx={{ flex: 1 }}>
          {children}
        </Box>

        {/* ── Footer ────────────────────────────────────────────────────── */}
        <Box
          component="footer"
          sx={{ bgcolor: '#002680', color: 'white', py: { xs: 4, md: 5 } }}
        >
          <Container maxWidth="lg">
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'center', md: 'flex-start' }}
              spacing={3}
            >
              <Box textAlign={{ xs: 'center', md: 'left' }}>
                <Box
                  component="img"
                  src="/os/Offshore Sails-Horizontal-Whiteout.svg"
                  alt="Offshore Sails"
                  sx={{ height: 36, mb: 1.5 }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: 'rgba(255,255,255,0.65)', maxWidth: 320 }}
                >
                  Precision-engineered Dacron sails, built in Barbados and shipped worldwide.
                </Typography>
              </Box>

              <Stack
                spacing={0.75}
                alignItems={{ xs: 'center', md: 'flex-end' }}
              >
                <Link
                  href="mailto:info@offshore-sails.com"
                  data-testid="footer-email"
                  sx={{
                    color: '#FFC726',
                    textDecoration: 'none',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  info@offshore-sails.com
                </Link>
                <Typography
                  variant="caption"
                  sx={{ color: 'rgba(255,255,255,0.45)' }}
                >
                  © {new Date().getFullYear()} Offshore Sails. All rights reserved.
                </Typography>
              </Stack>
            </Stack>
          </Container>
        </Box>

      </Box>
    </ThemeProvider>
  );
};
